import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useState, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DataverseFieldInfo } from "@shared/api-types";

interface FieldComboboxProps {
  fields: DataverseFieldInfo[] | undefined;
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  isLookupSuitableField?: (field: DataverseFieldInfo) => boolean;
  name?: string;
}

export const FieldCombobox = forwardRef<HTMLButtonElement, FieldComboboxProps>(({
  fields,
  value,
  onChange,
  isLoading = false,
  disabled = false,
  placeholder = "Select a field",
  isLookupSuitableField,
  name
}, ref) => {
  const [open, setOpen] = useState(false);

  // Function to extract display name from the Dataverse API response
  const getFieldDisplayName = (field: DataverseFieldInfo): string => {
    return field.DisplayName?.UserLocalizedLabel?.Label || 
           field.DisplayName?.LocalizedLabels?.[0]?.Label || 
           field.SchemaName || 
           field.displayName || 
           field.logicalName;
  };

  // Get the field type from either AttributeType or type
  const getFieldType = (field: DataverseFieldInfo): string | undefined => {
    return field.AttributeType || field.type;
  };

  // Find current selection display name
  const selectedField = fields?.find(field => field.logicalName === value);
  const selectedDisplayName = selectedField ? getFieldDisplayName(selectedField) : value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          name={name}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            disabled && "opacity-70 cursor-not-allowed",
            isLoading && "opacity-70"
          )}
          disabled={disabled}
        >
          {isLoading ? (
            <div className="flex items-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading fields...
            </div>
          ) : value && value !== "_default" && value !== "_none" ? (
            selectedDisplayName || value
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full min-w-[300px]">
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}...`} />
          <CommandEmpty>No field found.</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-y-auto">
            <CommandItem
              key="_default"
              value="_default"
              onSelect={() => {
                console.log("Selected default value");
                onChange("_default");
                setOpen(false);
              }}
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  value === "_default" ? "opacity-100" : "opacity-0"
                )}
              />
              {placeholder}
            </CommandItem>
            {placeholder === "Primary key (default)" && (
              <CommandItem
                key="_none"
                value="_none" 
                onSelect={() => {
                  console.log("Selected none value (primary key default)");
                  onChange("_none");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === "_none" ? "opacity-100" : "opacity-0"
                  )}
                />
                Primary key (default)
              </CommandItem>
            )}
            {fields?.map((field) => {
              const displayName = getFieldDisplayName(field);
              const fieldType = getFieldType(field);
              const isLookupSuitable = isLookupSuitableField && isLookupSuitableField(field);
              const fieldKey = field.logicalName || field.LogicalName || `field-${displayName}`; 
              
              return (
                <CommandItem
                  key={fieldKey}
                  value={fieldKey}
                  onSelect={() => {
                    const logicalName = field.logicalName ?? field.LogicalName ?? "";
                    console.log("Selected field:", logicalName);
                    onChange(logicalName);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === (field.logicalName || field.LogicalName) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center">
                    {isLookupSuitable && <span className="text-primary mr-1">★</span>}
                    <span>{displayName}</span>
                    {fieldType && (
                      <span className="text-muted-foreground ml-1 text-xs">
                        ({fieldType})
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
});